/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { useTheme } from "@material-ui/core/styles";
import { Backdrop, Grid, IconButton, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import types from "../../helpers/types";
import Loader from "../../components/Loader";
import UnsavedChangesModal from "../../components/common/UnsavedChangesModal";
import UserUpdateForm from "./UserUpdateForm";

const StyledHeader = styled(({ theme, ...props }) => <Grid {...props} />)`
  background-color: ${(props) => props.theme.palette.background.lightGrey};
  padding: 12px 16px;
`;

const StyledDiv = styled.div`
  padding: 16px;
  height: 100vh;
`;

function UserManagementDetails({
  setSelectedUserId,
  setShowUserUpdateSuccessNotification,
  setShowInviteResentSuccessNotification,
  unsavedModalOpen,
  setUnsavedModalOpen,
  setUnsavedChanges,
  unsavedChanges,
}) {
  const theme = useTheme();
  const userRequestStatus = useSelector((state) => state.user.requestStatus);
  const user = useSelector((state) => state.user.user);
  const loading = userRequestStatus === types.requestStatuses.inProgress;

  const handleCloseButtonClick = () => {
    if (unsavedChanges) {
      setUnsavedModalOpen(true);
    } else {
      setSelectedUserId(null);
    }
  };

  const handleModalAffirmative = () => {
    setUnsavedModalOpen(false);
  };

  const handleModalNegative = () => {
    setUnsavedModalOpen(false);
    setSelectedUserId(null);
    setUnsavedChanges(false);
  };

  return (
    <div
      data-testid="user-details"
      style={{ contain: "paint", minHeight: "100%" }}
    >
      <StyledHeader container alignItems="center" theme={theme}>
        <Grid item>
          <Typography variant="body1" component="h2">
            <span style={{ fontWeight: "bold" }}>Email:</span> {user?.email}
          </Typography>
        </Grid>
        <Grid item style={{ marginLeft: "auto" }}>
          <IconButton size="small" onClick={handleCloseButtonClick}>
            <CloseIcon
              fontSize="small"
              data-testid="user-details-close-button"
            />
          </IconButton>
        </Grid>
      </StyledHeader>
      <div>
        <Backdrop
          open={loading}
          style={{
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: "#fff",
            opacity: 1,
          }}
        >
          <Loader />
        </Backdrop>
        {!loading && (
          <StyledDiv data-testid="user-update">
            <UserUpdateForm
              setUnsavedChanges={setUnsavedChanges}
              setShowUserUpdateSuccessNotification={
                setShowUserUpdateSuccessNotification
              }
              setShowInviteResentSuccessNotification={
                setShowInviteResentSuccessNotification
              }
            />
          </StyledDiv>
        )}
      </div>
      <UnsavedChangesModal
        open={unsavedModalOpen}
        handleAffirmative={handleModalAffirmative}
        handleNegative={handleModalNegative}
        message="There are unsaved changes. Leaving will discard those changes."
      />
    </div>
  );
}

UserManagementDetails.propTypes = {
  selectedUserId: PropTypes.number.isRequired,
  setUnsavedModalOpen: PropTypes.func.isRequired,
  setUnsavedChanges: PropTypes.func.isRequired,
  setShowUserUpdateSuccessNotification: PropTypes.func.isRequired,
  setShowInviteResentSuccessNotification: PropTypes.func.isRequired,
  unsavedChanges: PropTypes.bool.isRequired,
  unsavedModalOpen: PropTypes.bool.isRequired,
};

export default UserManagementDetails;
